import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CalendarDays, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PAGES } from "@/i18n/extra_translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventGallery = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const pg = PAGES[lang] || PAGES.en;
  const [event, setEvent] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [touchX, setTouchX] = useState(null);

  useEffect(() => {
    axios.get(`${API}/past-events/${id}`).then((r) => setEvent(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => Math.min(i + 1, event.photos.length - 1));
      if (e.key === "ArrowLeft") setLightbox((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, event]);

  if (!event) {
    return <main className="min-h-screen flex items-center justify-center text-sm text-[#5C7368]" data-testid="gallery-loading">Loading…</main>;
  }

  const onTouchEnd = (e) => {
    if (touchX === null) return;
    const delta = e.changedTouches[0].clientX - touchX;
    if (delta < -50) setLightbox((i) => Math.min(i + 1, event.photos.length - 1));
    if (delta > 50) setLightbox((i) => Math.max(i - 1, 0));
    setTouchX(null);
  };

  return (
    <main className="pt-28 pb-20 px-6" data-testid="event-gallery-page">
      <div className="max-w-6xl mx-auto">
        <a href="/past-events" data-testid="gallery-back-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#1E352A]/25 px-6 py-2.5 text-xs tracking-[0.12em] uppercase text-[#1E352A] transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261]">
          <ArrowLeft size={14} /> {pg.back_events}
        </a>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mt-8">
          <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-[#1A2F24]" data-testid="gallery-title">{event.title}</h1>
          <p className="mt-3 flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#F4A261]">
            <CalendarDays size={13} /> {event.date}
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5C7368]">{event.description}</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="gallery-grid">
          {event.photos.map((photo, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              onClick={() => setLightbox(i)}
              data-testid={`gallery-photo-${i}`}
              className="rounded-2xl overflow-hidden border border-[#E2E8E4] cursor-zoom-in group"
            >
              <img src={photo} alt={`${event.title} photo ${i + 1}`} className="w-full h-52 sm:h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#1A2F24]/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
            onTouchEnd={onTouchEnd}
            data-testid="gallery-lightbox"
          >
            <button onClick={() => setLightbox(null)} data-testid="lightbox-close" aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300">
              <X size={20} />
            </button>
            {lightbox > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }} data-testid="lightbox-prev" aria-label="Previous photo"
                className="absolute left-3 sm:left-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300 z-10">
                <ChevronLeft size={22} />
              </button>
            )}
            {lightbox < event.photos.length - 1 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }} data-testid="lightbox-next" aria-label="Next photo"
                className="absolute right-3 sm:right-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] hover:bg-[#F4A261] transition-colors duration-300 z-10">
                <ChevronRight size={22} />
              </button>
            )}
            <img
              src={event.photos[lightbox]}
              alt={`${event.title} photo ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-2xl object-contain"
              data-testid="lightbox-image"
            />
            <p className="absolute bottom-5 text-xs tracking-[0.2em] text-[#C4D7D1]" data-testid="lightbox-counter">
              {lightbox + 1} / {event.photos.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default EventGallery;
